import type { Backend as AcquisitUIBackend, Product, Person, ProductActionCompound, ImageCollection, InputImage, MpidRequest, MpidResult, FilesInputFile, FileUploadResult } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/library'
import type { OrderResponse, UIResponse, CreateImageCollectionResponse, CreateImageResponse, BankIDDataResponse, ElhubConsentResponse, ElhubConsentData } from '@/lib/backend-cors/types'
import { Domain, DomainRouter } from '@/lib/domain-router'
import config from '@config'
import { usePylon } from '@/lib/backend-cors/pylon'
import { useAsgard } from '@/lib/backend-cors/asgard'
import { usePortal } from '@/lib/backend-cors/portal'
import type { Origin } from '@/stores/generic'
import { useAPIStore } from '@/stores/api'
import APIError from '@/lib/APIError'
import { addImage, addImageCollection, deleteImage, deleteImageCollection, updateImageCollection } from '@/lib/backend-cors/images'
import type { useAxios } from '@/lib/backend-cors/axios'
import type { ProtocolSubmission } from '@/stores/protocol.ts'
import { useProtocolStore } from '@/stores/protocol.ts'
import type { AxiosResponse } from 'axios'

/**
 * An interface for a Backend
 */
export interface Backend {
	/**
	 * Developer-friendly name of this backend, used for debugging purposes
	 */
	get label(): string
	
	/**
	 * Base URL this backend points to
	 */
	get baseURL(): string
	
	/**
	 * Get the protocol's {@link Origin}
	 */
	getProtocolOrigin(ticketID: string, password: string): Promise<Origin>
	getProtocolOrigin(token: string): Promise<Origin>
	
	/**
	 * Get an order by its ticket
	 *
	 * @param ticketID  Ticket ID
	 * @param password  Password for the ticket
	 */
	getOrderByTicket(ticketID: string, password: string): Promise<OrderResponse>
	
	/**
	 * Get an order using an API-provided token
	 *
	 * @param token  Token
	 */
	getOrderByToken(token: string): Promise<OrderResponse>
	
	/**
	 * Get the UI definition for a given token
	 *
	 * @param token  Token
	 */
	getUI(token: string): Promise<UIResponse>
	
	/**
	 * Submit a protocol for a given token
	 *
	 * @param token    Token
	 * @param payload  JSON Payload
	 */
	submitProtocol(token: string, payload: Record<string, any>): Promise<AxiosResponse>
	
	/**
	 * Get the latest available state backup for a given token
	 * Returns either a backup or null.
	 *
	 * @param token  Token
	 */
	getLatestBackup(token: string): Promise<Record<string, any>|null>
	
	storeBackup(token: string, backup: any): Promise<void>
	
	deleteBackup(token: string): Promise<void>
	
	/**
	 * Create an image collection
	 *
	 * @param token       Protocol Token
	 * @param title       Title of the collection
	 * @param semantic    Semantic value
	 * @param metadata    Metadata
	 */
	createImageCollection(token: string, title: string|null, semantic: string|null, metadata: Record<string, any>|null): Promise<CreateImageCollectionResponse>
	
	/**
	 * Update an image collection
	 *
	 * @param token
	 * @param imageCollectionID
	 * @param title
	 * @param metadata
	 */
	updateImageCollection(token: string, imageCollectionID: string|number, title: string|null, metadata: Record<string, any>|null): Promise<void>
	
	/**
	 * Upload an image to an image collection
	 *
	 * @param token               Protocol Token
	 * @param imageCollectionID   Image Collection ID
	 * @param image               Image as a data URI
	 * @param title               Title of the image
	 */
	uploadImage(token: string, imageCollectionID: string|number, image: string, title: string|null): Promise<CreateImageResponse>
	
	/**
	 * Delete an image collection
	 *
	 * @param token               Protocol Token
	 * @param imageCollectionID   Image Collection ID
	 */
	deleteImageCollection(token: string, imageCollectionID: string|number): Promise<void>
	
	/**
	 * Delete an image
	 *
	 * @param token               Protocol Token
	 * @param imageCollectionID   Image Collection ID it was uploaded for
	 * @param imageID             Image ID
	 */
	deleteImage(token: string, imageCollectionID: string|number, imageID: string|number): Promise<void>
	
	/**
	 * Get the URL to redirect to when user initiates bankID signing
	 *
	 * @param token        Protocol Token
	 * @param personID     Person's acquisit-assigned ID {@link Person.id}
	 * @param personNumber Person number to pre-fill for BankID
	 * @param returnURL    The URL to return back to after signing. The bankID signature service automatically
	 *                     appends a query param "status" with value "success", "abort" or "error". In case of
	 *                     "success", an additional query param "sessionId" is added to pass to {@link getBankIDData}
	 */
	getBankIDURL(token: string, personID: string, personNumber: string, returnURL: string): string
	
	/**
	 * Get the signature data for a bankID signing session
	 *
	 * @param token        Protocol Token
	 * @param personID     Person's acquisit-assigned ID {@link Person.id}
	 * @param sessionID    The signing session's ID, {@link getBankIDURL}
	 */
	getBankIDData(token: string, personID: string, sessionID: string): Promise<BankIDDataResponse>
	
	/**
	 * Get the terms for a specific product
	 *
	 * @param token       Protocol Token
	 * @param product     Product ID
	 */
	getProductTerms(token: string, product: Product): Promise<string|null>
	
	/**
	 * Fetch Målepunkt ID
	 */
	fetchMpid(token: string, request: MpidRequest): Promise<MpidResult[]>
	
	/**
	 * Create an Elhub consent session
	 *
	 * @param {string} token
	 * @param {ProtocolSubmission} submission
	 * @param {string} returnURL
	 *
	 * @returns {Promise<ElhubConsentResponse>}
	 */
	createConsent(token: string, submission: ProtocolSubmission, returnURL: string): Promise<ElhubConsentResponse>
	
	getConsentData(token: string, sessionID: string): Promise<ElhubConsentData>
	
	submitReview(token: string, score: number, comment: string): Promise<void>
	
	/**
	 * Parse an error that the backend returned into a detailed object
	 */
	parseError(error: any): any
	
	/**
	 * Handle an error
	 */
	onError(error: any): any
	
	retry: ReturnType<typeof useAxios>['retry']
}

let _currentBackend: Backend|null = null

/**
 * Determine which backend to use for the current context
 */
export const detectBackend = () => {
	const currentDomain = DomainRouter.get()
	
	switch (currentDomain.id) {
		case Domain.DIGITALOVERTAKELSE:
		case Domain.DIGITALDOKUMENT:
		case Domain.OPPGJORSSKJEMA:
		case Domain.ENKLEREFLYTTING:
			return usePylon()
			
		default:
			// Check for preview, otherwise it's asgard
			if (window.location.pathname.substring(0, 8) == '/preview') {
				return usePortal()
			} else {
				return useAsgard()
			}
	}
}

switch (config.backend.override) {
	case 'asgard':
		_currentBackend = useAsgard()
		break

	case 'pylon':
		_currentBackend = usePylon()
		break
	
	case 'portal':
		_currentBackend = usePortal()
		break
	
	default:
		// auto-detect
		_currentBackend = detectBackend()
}

export const useBackend = (): Backend => {
	return _currentBackend!
}

// AcquisitUI adapter
export const createAcquisitUIBackendAdapter = (backend: Backend): AcquisitUIBackend => ({
	async getProductTerms(product: Product, _?: ProductActionCompound): Promise<string> {
		const terms = await backend.getProductTerms(useAPIStore().token!, product)
		
		return terms ?? ''
	},
	
	addImageCollection(collection: ImageCollection, metadata: Record<string, any>, semantic?: string | null): Promise<any> {
		return addImageCollection(backend, useAPIStore().token!, semantic ?? null, metadata, collection)
	},
	
	addImage(collectionID: string | number, image: InputImage): Promise<any> {
		return addImage(backend, useAPIStore().token!, collectionID, image)
	},
	
	updateImageCollection(collectionID: string | number, title: string | null, metadata: Record<string, any>): Promise<any> {
		return updateImageCollection(backend, useAPIStore().token!, collectionID, metadata)
	},
	
	deleteImage(collectionID: string | number, image: string | number): Promise<void> {
		return deleteImage(backend, useAPIStore().token!, collectionID, image)
	},
	
	deleteImageCollection(collectionID: string | number): Promise<void> {
		return deleteImageCollection(backend, useAPIStore().token!, collectionID)
	},
	
	async fetchMpid(request: MpidRequest): Promise<MpidResult[]> {
		const protocolStore = useProtocolStore()
		request.postcode = protocolStore.order?.address.postcode ?? undefined
		
		return useBackend().fetchMpid(useAPIStore().token!, request)
	},
	
	async uploadFile(file: FilesInputFile): Promise<FileUploadResult> {
		throw new APIError('Not implemented, yet', 400)
	},
	
	async deleteFile(file: FilesInputFile): Promise<void> {
		throw new APIError('Not implemented, yet', 400)
	}
})

export const useAcquisitUIBackendAdapter = () => createAcquisitUIBackendAdapter(useBackend())